/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-present Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import 'whatwg-fetch';
import React from 'react';
import ReactDOM from 'react-dom';
import deepForceUpdate from 'react-deep-force-update';
import queryString from 'query-string';
import { createPath } from 'history/PathUtils';
import * as Sentry from '@sentry/browser';
import { CaptureConsole } from '@sentry/integrations';
import App from './components/App';
// import ErrorBoundary from './components/common/ErrorBoundary';
import createFetch from './core/createFetch';
import configureStore from './store/configureStore';
import history from './core/history';
import { searchInit } from './actions/search'
import { checkIEMode } from './core/utils'
import urlGeneratorHelper from './core/urlGeneratorHelper';
import router from './router';

Sentry.init({
  dsn: 'https://3d5c2bfefa094a639e3e7562587f2ef0@jpksentry.youdao.com/9',
  integrations: [
    new CaptureConsole({
      levels: ['error'],
    }),
  ],
});

/* eslint-disable global-require */

// Global (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html

const ie = checkIEMode(window.navigator.userAgent)
const clientFetch = createFetch(self.fetch, {
  ie,
})
const queryParams = queryString.parse(location.search)
const clientStore = configureStore(window.App.state, {
  fetch: clientFetch,
  history,
  originParams: queryParams,
})
const context = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  insertCss: (...styles) => {
    // eslint-disable-next-line no-underscore-dangle
    const removeCss = styles.map(x => x._insertCss());
    return () => { removeCss.forEach(f => f()); };
  },
  // http://redux.js.org/docs/basics/UsageWithReact.html
  store: clientStore,
  fetch: clientFetch,
  query: queryParams,
  urlGenerator: urlGeneratorHelper(queryParams),
};

// Switch off the native scroll restoration behavior and handle it manually
// https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
const scrollPositionsHistory = {};
if (window.history && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

let onRenderComplete = function initialRenderComplete() {
  const elem = document.getElementById('css');
  if (elem) elem.parentNode.removeChild(elem);
  onRenderComplete = function renderComplete(route, location) {
    document.title = route.title;
    // 对齐页面 mate 内容
    const metaDoms = document.getElementsByTagName('meta');
    for (let i = 0; i < metaDoms.length; i++) {
      switch (metaDoms[i].getAttribute('name')) {
        case 'description':
          metaDoms[i].setAttribute('content', route.description);
          break;
        case 'keywords':
          metaDoms[i].setAttribute('content', route.keywords);
          break;
        default: {
          break;
        }
      }
    }

    // Update necessary tags in <head> at runtime here, ie:
    // updateMeta('keywords', route.keywords);
    // updateCustomMeta('og:url', route.canonicalUrl);
    // updateCustomMeta('og:image', route.imageUrl);
    // updateLink('canonical', route.canonicalUrl);
    // etc.

    let scrollX = 0;
    let scrollY = 0;
    const pos = scrollPositionsHistory[location.key];
    if (pos) {
      scrollX = pos.scrollX;
      scrollY = pos.scrollY;
    } else {
      const targetHash = location.hash.substr(1);
      if (targetHash) {
        const target = document.getElementById(targetHash);
        if (target) {
          scrollY = window.pageYOffset + target.getBoundingClientRect().top;
        }
      }
    }

    // Restore the scroll position if it was saved into the state
    // or scroll to the given #hash anchor
    // or scroll to top of the page
    window.scrollTo(scrollX, scrollY);

    // Google Analytics tracking. Don't send 'pageview' event after
    // the initial rendering, as it was already sent
    if (window.ga) {
      window.ga('send', 'pageview', createPath(location));
    }
  };
};

const container = document.getElementById('app');
let appInstance;
let currentLocation = history.location;

// Re-render the app when window.location changes
async function onLocationChange(location, action) {
  const curTime = Date.now();
  const grayStartTime = new Date(2020, 3, 4).getTime();
  const grayEndTime = new Date(2020, 3, 5).getTime();
  const filterStyle = window.location.pathname === '/' ?
    'grayscale(1)' : 'unset';
  if (curTime > grayStartTime && curTime < grayEndTime) {
    document.documentElement.style.filter = filterStyle;
  }

  // Remember the latest scroll position for the previous location
  scrollPositionsHistory[currentLocation.key] = {
    scrollX: window.pageXOffset,
    scrollY: window.pageYOffset,
  };
  // Delete stored scroll position for next page if any
  if (action === 'PUSH') {
    delete scrollPositionsHistory[location.key];
  }
  currentLocation = location;

  try {
    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    const route = await router.resolve({
      ...context,
      path: location.pathname,
      query: queryParams,
      pathName: location.pathname,
    });

    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key) {
      return;
    }

    if (route.redirect) {
      history.replace(route.redirect);
      return;
    }

    appInstance = ReactDOM.render(
      <App context={context}>{route.component}</App>,
      container,
      () => onRenderComplete(route, location),
    );
  } catch (error) {
    if (__DEV__) {
      throw error;
    }

    console.error(error);

    // Do a full page reload if error occurs during client-side navigation
    if (action && currentLocation.key === location.key) {
      window.location.reload();
    }
  }
}

// Handle client-side navigation by using HTML5 History API
// For more information visit https://github.com/mjackson/history#readme
history.listen(onLocationChange);
onLocationChange(currentLocation);

clientStore.dispatch(searchInit())

// Enable Hot Module Replacement (HMR)
if (module.hot) {
  module.hot.accept('./router', () => {
    if (appInstance) {
      // Force-update the whole tree, including components that refuse to update
      deepForceUpdate(appInstance);
    }

    onLocationChange(currentLocation);
  });
}



// WEBPACK FOOTER //
// ./src/client.js